/* global videojs, io */
import './video.js/dist/video-js.css';
import * as mediasoupClient from 'mediasoup-client';

import { initializePlayerEvents } from './events/playerEvents';
import './events/windowEvents';

import InfoPopup from './components/info-popup';
import exitButton from './components/exit-button';
import switchAudioButton from './components/audio-tracks-menu';
import switchCameraButton from './components/camera-tracks-menu';
import switchQualityButton from './components/quality-levels-menu';
import SubtitlesMenuButton from './components/closed-captions-menu';
import OnscreenControls from './components/onscreen-controls';

import {
  generateAndPreloadVideoList,
  safelyRemoveControlBarChild,
  setBigPlayButtonVisibility
} from './utils/utils';
import { enablePinchZoom, setVideoContainerHeight } from './render/screen-actions';
import {
  dashMediaPlayerLiveSettings,
  dashMediaPlayerSettings,
  dashMediaPlayerSettingsSyncX,
  initVROptions,
  playerOptions,
  seekButtonsOptions,
  vrOptions,
  xrOptions
} from './utils/player-options';
import { fetchOverlaysAndUpdatePlayer } from './handlers/overlays-handlers';

import {
  checkWebRTCAvailability,
  getSocketBaseUrl,
  debugTracks,
  monitorFrames,
  forcePlay,
  msSwitchTrack
} from './utils/webrtc-utils';

function applyTechGetAlias() {
  const vjs = window.videojs;
  if (!vjs?.Player?.prototype) return;
  const proto = vjs.Player.prototype;

  if (typeof proto.techGet !== 'function' && typeof proto.techGet_ === 'function') {
    proto.techGet = function (...args) {
      return this.techGet_(...args);
    };
    console.log('[MS-Player] 🧩 techGet alias applied');
  }
}

if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', applyTechGetAlias, { once: true });
  } else {
    applyTechGetAlias();
  }
}

const log = (...a) => console.log('[MS-Player]', ...a);

const intorealityPlayer = function () {
  const player = this;
  const videoElement = player.tech_.el();

  if (!window.isWebRTC) {
    if (window.MediaSource && window.sourcesDash?.trim()) {
      window.sources = [window.sourcesDash];
      player.src({ src: window.sources[0], type: 'application/dash+xml' });
      window.isDashSource = true;
    } else {
      player.playlist(generateAndPreloadVideoList());
      player.playlist.currentItem(0);
    }
  }

  let socket, device, recvTransport, allConsumers, currentTrack;

  if (window.isWebRTC) {
    const renderStream = new MediaStream();
    videoElement.srcObject = renderStream;
    videoElement.muted = true;

    allConsumers = { video: {}, audio: {} };
    currentTrack = { video: null, audio: null };
    window.allConsumers = allConsumers;

    const baseURL = getSocketBaseUrl(window.webrtcManifestUrl);
    socket = io(baseURL, { transports: ['websocket'] });

    window.msSwitchTrack = (kind, trackId) => {
      msSwitchTrack(kind, trackId, allConsumers, renderStream, socket, currentTrack);
    };

    socket.emit('getRtpCapabilities', null, async routerCaps => {
      device = new mediasoupClient.Device();
      await device.load({ routerRtpCapabilities: routerCaps });

      socket.emit('createTransport', null, async params => {
        recvTransport = device.createRecvTransport(params);

        let lastState;
        recvTransport.on('connectionstatechange', s => {
          if (s !== lastState) {
            lastState = s;
            log('🔁 transport', s);
          }
        });

        recvTransport.on('connect', ({ dtlsParameters }, cb, errCb) => {
          socket.emit('connectTransport', { dtlsParameters }, ok =>
            ok ? cb() : errCb(new Error('DTLS refused'))
          );
        });

        socket.emit('consumeAll', { rtpCapabilities: device.rtpCapabilities }, async allTracks => {
          const firstOfKind = { video: true, audio: true };

          for (const kind of ['video', 'audio']) {
            for (const [id, p] of Object.entries(allTracks[kind])) {
              const consumer = await recvTransport.consume({ ...p, paused: true });
              allConsumers[kind][id] = consumer;
              log(`🆕 Consumer created (${kind})`, consumer);

              if (!renderStream.getTracks().includes(consumer.track)) {
                renderStream.addTrack(consumer.track);
                log(`📡 Track added to stream (${kind})`, consumer.track);
              }

              if (firstOfKind[kind]) {
                firstOfKind[kind] = false;
                consumer.resume();
                log(`⏯ Resumed consumer (${kind})`, consumer);

                if (kind === 'video') {
                  forcePlay(videoElement);
                  monitorFrames(videoElement);
                }
                ensureCommonUI();
              }
            }
          }

          const firstVid = Object.keys(allConsumers.video)[0];
          const firstAud = Object.keys(allConsumers.audio)[0];
          if (firstVid) window.msSwitchTrack('video', firstVid);
          if (firstAud) window.msSwitchTrack('audio', firstAud);

          fetch(window.webrtcManifestUrl)
            .then(r => r.json())
            .then(manifest => {
              window.webRtcVideoTrackIds = manifest.video.map(t => t.id);
              window.webRtcAudioTrackIds = manifest.audio.map(t => t.id);
            });
        });
      });
    });

    player.on('dispose', () => {
      socket?.close();
      for (const k of ['video', 'audio'])
        for (const c of Object.values(allConsumers[k] || {})) c.close();
      recvTransport?.close();
    });
  }

  if (videojs.browser.IS_IOS) player.vr(vrOptions);
  else player.xr(xrOptions);

  let uiAttached = false;
  const ensureCommonUI = () => {
    if (uiAttached) return;
    uiAttached = true;

    setVideoContainerHeight();
    window.dashMediaPlayer = player.dash && player.dash.mediaPlayer;
    if (dashMediaPlayer) {
      let s = dashMediaPlayerSettings;
      if (player.duration() === Infinity) s = dashMediaPlayerLiveSettings;
      else if (window.isSyncX) s = dashMediaPlayerSettingsSyncX;
      dashMediaPlayer.updateSettings(s);
      window.isSyncX = false;
    }

    const exitBtn = player.addChild('ExitButtonPlugin', {});
    player.el().appendChild(exitBtn.el());

    const cb = player.getChild('controlBar');

    if (window.isWebRTC) {
      safelyRemoveControlBarChild(player, 'PlayToggle');
    } else {
      new OnscreenControls(player, { seekButtonsOptions });
    }

    cb.addChild('SwitchCameraButton', {});
    cb.addChild('SwitchAudioButton', {});
    cb.addChild('SubtitlesMenuButton', {});
    cb.addChild('SwitchQualityButton', {});

    ['PictureInPictureToggle', 'ChaptersButton', 'subsCapsButton'].forEach(b =>
      safelyRemoveControlBarChild(player, b)
    );

    cb.addChild('InfoPopup', {});

    enablePinchZoom(player);
    setBigPlayButtonVisibility(player, true);
    initializePlayerEvents(player);

    if (window.isWebRTC) {
      const isiOS = videojs.browser.IS_IOS;
      let firstUserPlay = true;

      const unmuteOnFirstPlay = () => {
        if (!firstUserPlay) return;
        firstUserPlay = false;
        if (!isiOS) videoElement.muted = false;
        videoElement.play().catch(err => log('⚠️ unmute play() failed:', err));
      };

      player.on('play', unmuteOnFirstPlay);

      if (isiOS) {
        const iosGesturePlay = () => {
          log('👆 iOS touch gesture detected — attempting play');
          videoElement.play().catch(err => log('⚠️ iOS play() failed:', err));
          document.removeEventListener('touchend', iosGesturePlay);
          document.removeEventListener('click', iosGesturePlay);
        };
        document.addEventListener('touchend', iosGesturePlay, { once: true });
        document.addEventListener('click', iosGesturePlay, { once: true });
      }

      const attachPlayProxy = (el, name) => {
        if (!el) return;
        const playVideo = () => {
          log(`🟡 UI interaction on ${name}`);
          const p = videoElement.play();
          if (p && p.catch) p.catch(e => log('⚠️ play() failed:', e));
        };
        el.addEventListener('click', playVideo, { capture: true });
        el.addEventListener('touchend', playVideo, { capture: true });
      };

      attachPlayProxy(player.el().querySelector('.vjs-big-vr-play-button'), 'BigPlayVR');
      attachPlayProxy(player.controlBar?.getChild('PlayToggle')?.el(), 'ControlBar PlayToggle');
      attachPlayProxy(
        player.el().querySelector('.vjs-button-container .vjs-play-control'),
        'Onscreen PlayToggle'
      );
    }
  };

  if (!window.isWebRTC) player.one('loadedmetadata', ensureCommonUI);
  if (window.isWebRTC) player.one('canplay', ensureCommonUI);
};

videojs.registerPlugin('intorealityPlayer', intorealityPlayer);

export async function initIRPlayer(playerId, eventInformation) {
  window.audioNames = eventInformation.audio_names;
  window.camNames = eventInformation.cam_names;
  window.camImages = eventInformation.cam_imgs;
  window.audioImages = eventInformation.audio_imgs;
  window.captionsImages = eventInformation.captions_imgs;
  window.videoTypes = eventInformation.video_types;
  window.allowedReferers = eventInformation.allowed_referers;
  window.canBeIframe = eventInformation.can_be_iframe;
  window.productId = eventInformation.product_id;
  window.sources = eventInformation.sources;
  window.sourcesDash = eventInformation.sources_dash;

  window.webrtcManifestUrl =
    typeof eventInformation.webrtc_manifest === 'string' && eventInformation.webrtc_manifest.trim()
      ? eventInformation.webrtc_manifest.trim()
      : null;

  window.isWebRTC = await checkWebRTCAvailability(window.webrtcManifestUrl);

  const player = videojs(playerId, playerOptions);
  initVROptions();
  player.intorealityPlayer();
}

window.initIRPlayer = initIRPlayer;
export default intorealityPlayer;